import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Avatar,
  Chip,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Paper,
  InputBase,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  LocalHospital as HospitalIcon,
  CalendarMonth as CalendarIcon,
  Star as StarIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { RootState, AppDispatch } from '../../store';
import { fetchDoctors } from '../../store/slices/doctorSlice';

interface Doctor {
  id: number;
  fullName: string;
  specialization: string;
  qualification: string;
  experienceYears: number;
  rating: number;
  totalReviews: number;
  status: string;
  statusDisplayName: string;
  shiftStartTime: string;
  shiftEndTime: string;
  availableDays: string;
}

const specializations = [
  'All',
  'General Medicine',
  'Cardiology',
  'Dermatology',
  'Neurology',
  'Orthopedics',
  'Pediatrics',
  'Psychiatry',
  'Gynecology',
  'Ophthalmology',
  'ENT',
  'Dental',
];

const DoctorListPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { doctors, loading, error } = useSelector((state: RootState) => state.doctor);

  const [searchQuery, setSearchQuery] = useState('');
  const [specializationFilter, setSpecializationFilter] = useState('All');

  useEffect(() => {
    dispatch(fetchDoctors() as any);
  }, [dispatch]);

  const filteredDoctors = (doctors || []).filter((doctor: Doctor) => {
    const matchesSearch =
      !searchQuery ||
      doctor.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doctor.specialization?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSpecialization = specializationFilter === 'All' || doctor.specialization === specializationFilter;
    return matchesSearch && matchesSpecialization && doctor.status === 'APPROVED';
  });

  const getAvailabilityColor = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'success';
      case 'PENDING': return 'warning';
      case 'REJECTED': return 'error';
      default: return 'default';
    }
  };

  const formatAvailableDays = (days: string) => {
    if (!days) return 'Not specified';
    return days.split(',').join(', ');
  };

  return (
    <Box sx={{ p: 2, flex: 1, overflow: 'auto' }}>
      {/* Header */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#111827' }}>Available Doctors</Typography>
          <Typography variant="body2" color="#6b7280">Select a doctor to book your appointment</Typography>
        </Box>
        <Typography variant="body2" color="#6b7280">{filteredDoctors.length} doctor{filteredDoctors.length !== 1 ? 's' : ''} found</Typography>
      </Box>

      {/* Search & Filter */}
      <Paper sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid #e5e7eb' }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by doctor name or specialization..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#94a3b8' }} /></InputAdornment>,
                endAdornment: searchQuery && <InputAdornment position="end"><IconButton size="small" onClick={() => setSearchQuery('')}><ClearIcon fontSize="small" /></IconButton></InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 1.5, bgcolor: 'transparent' } }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Specialization</InputLabel>
              <Select value={specializationFilter} label="Specialization" onChange={(e) => setSpecializationFilter(e.target.value)} sx={{ borderRadius: 1.5 }}>
                {specializations.map((spec) => (<MenuItem key={spec} value={spec}>{spec}</MenuItem>))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button fullWidth variant="outlined" onClick={() => { setSearchQuery(''); setSpecializationFilter('All'); }} sx={{ borderRadius: 1.5 }}>Clear</Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Error */}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Loading */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      )}

      {/* Empty State */}
      {!loading && filteredDoctors.length === 0 && (
        <Card sx={{ borderRadius: 2, border: '1px solid #e5e7eb' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <HospitalIcon sx={{ fontSize: 48, color: '#d1d5db', mb: 1 }} />
            <Typography variant="h6" color="#6b7280">No doctors found</Typography>
            <Typography variant="body2" color="#9ca3af">Try adjusting your search or filter criteria</Typography>
          </CardContent>
        </Card>
      )}

      {/* Doctors Grid */}
      {!loading && filteredDoctors.length > 0 && (
        <Grid container spacing={2}>
          {filteredDoctors.map((doctor: Doctor) => (
            <Grid item xs={12} sm={6} lg={4} xl={3} key={doctor.id}>
              <Card sx={{ borderRadius: 2, border: '1px solid #e5e7eb', height: '100%', display: 'flex', flexDirection: 'column', transition: 'all 0.2s', '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.1)', borderColor: '#d1d5db' } }}>
                <CardContent sx={{ p: 2, flex: 1, display: 'flex', flexDirection: 'column' }}>
                  {/* Doctor Avatar & Name */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                    <Avatar sx={{ width: 48, height: 48, bgcolor: 'transparent' , fontSize: '1.25rem', mr: 1.5 }}>{doctor.fullName?.charAt(0) || 'D'}</Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#111827' }} noWrap>{doctor.fullName}</Typography>
                      <Chip label={doctor.specialization} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'transparent' , color: '#2563eb' }} />
                    </Box>
                  </Box>

                  {/* Qualification */}
                  <Typography variant="body2" color="#6b7280" sx={{ mb: 1.5 }}>{doctor.qualification}</Typography>

                  {/* Stats */}
                  <Box sx={{ display: 'flex', gap: 2, mb: 1.5 }}>
                    <Box>
                      <Typography variant="caption" color="#9ca3af">Experience</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>{doctor.experienceYears} yrs</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <StarIcon sx={{ color: '#f59e0b', fontSize: 16, mr: 0.25 }} />
                      <Typography variant="body2" sx={{ fontWeight: 500, color: '#111827' }}>{doctor.rating?.toFixed(1) || '4.5'}</Typography>
                    </Box>
                  </Box>

                  {/* Availability */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="caption" color="#9ca3af">Available Days</Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.75rem', color: '#374151' }}>{formatAvailableDays(doctor.availableDays)}</Typography>
                    <Typography variant="caption" color="#9ca3af">Timing: {doctor.shiftStartTime?.substring(0, 5)} - {doctor.shiftEndTime?.substring(0, 5)}</Typography>
                  </Box>

                  {/* Status */}
                  <Box sx={{ mb: 1.5 }}>
                    <Chip label={doctor.statusDisplayName || 'Available'} size="small" color={getAvailabilityColor(doctor.status) as 'success' | 'warning' | 'error' | 'default'} />
                  </Box>

                  {/* Action */}
                  <Box sx={{ mt: 'auto' }}>
                    <Button fullWidth variant="contained" startIcon={<CalendarIcon />} onClick={() => navigate(`/appointments/book?doctorId=${doctor.id}`)} sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}>Book Appointment</Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default DoctorListPage;